// src/pages/try-editor/use-claim-try-diagrams.ts
import i18next from 'i18next';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { TRY_EDITORS_ENABLED } from '@/config/try-editors';
import type { DiagramStore } from '@/diagram-persistence/diagram-store';
import { getDiagramStore } from '@/diagram-persistence/store-instance';
import { getAuthIdentity } from '@/doc-sync/auth-identity';

import { claimTryDiagrams } from './claim-try-diagrams';
import { getTryDiagramStore } from './try-store';

/**
 * Thin wiring between auth state and `claimTryDiagrams`. Takes `isSignedIn`/
 * `userId` as plain arguments (Clerk's `useAuth()` values) rather than
 * calling `useAuth()` itself, for two reasons: it keeps this hook testable
 * without mocking `@clerk/clerk-react` (bun's `mock.module` is
 * process-global and leaks across test files — see repo convention), and it
 * lets the caller control exactly *when* this fires.
 *
 * Toast text is resolved via the `i18next` singleton directly (`i18next.t`)
 * rather than `useTranslation()`: this hook doesn't render anything, so
 * subscribing the whole component tree it's called from (`InnerApp`) to
 * every language change — and therefore re-rendering `RouterProvider` on a
 * live-preview language switch — bought nothing.
 *
 * ORDERING + FAIL-CLOSED: this must be called from `InnerApp` (src/index.tsx)
 * as a hook AFTER the effect that calls `setAuthTokenProvider`. React runs a
 * component's effects in declaration order within one commit, so as long as
 * this hook's `useEffect` is declared later in the same component, it is
 * guaranteed to run after the identity is registered — meaning
 * `getDiagramStore()` (the default `accountStoreFactory`) already returns
 * the synced (local+remote) store, not the pre-sign-in local-only one. Firing
 * this from an independent effect elsewhere (e.g. keyed only on isSignedIn in
 * a different component) would race that registration and risk claiming into
 * the wrong store. The `getAuthIdentity() == null` guard below is defense in
 * depth against exactly that: if a future refactor moves this hook earlier
 * (or into a different effect ordering), it fails CLOSED — skipping the
 * claim — instead of failing OPEN and copying into the unsynced local store
 * (then deleting the try-store source, losing nothing outright but stranding
 * the copy pre-sync).
 *
 * Gated by `TRY_EDITORS_ENABLED` (default parameter `enabled`, overridable
 * for tests): with the launch flag off, no signed-in app load should open or
 * create the `azabu-try-diagrams` database at all, since the /try routes
 * themselves are unreachable and nothing can be waiting there in normal
 * operation.
 *
 * Runs on sign-IN as well as sign-UP (both flip `isSignedIn` to true), and
 * again on an account switch on a shared browser (`userId` changing) so a
 * second identity's try work — if any was made in between — gets its chance
 * too. Non-blocking: fire-and-forget, never gates rendering.
 */
export function useClaimTryDiagrams(
    isSignedIn: boolean | undefined,
    userId: string | null | undefined,
    accountStoreFactory: () => DiagramStore | null = getDiagramStore,
    tryStoreFactory: () => DiagramStore | null = getTryDiagramStore,
    enabled: boolean = TRY_EDITORS_ENABLED,
): void {
    const accountStoreFactoryRef = useRef(accountStoreFactory);
    accountStoreFactoryRef.current = accountStoreFactory;
    const tryStoreFactoryRef = useRef(tryStoreFactory);
    tryStoreFactoryRef.current = tryStoreFactory;

    useEffect(() => {
        if (!isSignedIn) return;
        if (!enabled) return;
        // Fail-closed identity guard — see the ORDERING + FAIL-CLOSED note
        // above. isSignedIn can be true (Clerk's own state) a tick before
        // this app's identity registry (setAuthTokenProvider) catches up if
        // effect ordering is ever disturbed; never claim against that gap.
        if (getAuthIdentity() == null) return;
        let cancelled = false;
        claimTryDiagrams(tryStoreFactoryRef.current(), accountStoreFactoryRef.current()).then(
            ({ claimed, failed }) => {
                // Only report success for what actually succeeded: stay
                // silent if a document failed to claim, even when others
                // succeeded — a toast promising "your work was added" while
                // something is still stuck (and silently retried on the next
                // sign-in) would be misleading.
                if (!cancelled && claimed > 0 && failed === 0) {
                    toast.success(i18next.t('tryEditor.claimed'));
                }
            },
            () => {
                // claimTryDiagrams is designed to never reject, but guard
                // anyway — a claim failure must never surface as an
                // unhandled rejection or break sign-in.
            },
        );
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- factories read via ref on purpose (see use-try-singleton-diagram.ts for the same pattern); `enabled` is a build-time constant by default and never changes within a session
    }, [isSignedIn, userId]);
}
